export interface CalendarEvent {
    id: string;
    title: string;
    description: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    createdBy: string;
    createdAt: Date;
    updatedAt: Date;
    attendees?: string[];
    location?: string;
    isRecurring?: boolean;
    recurrencePattern?: RecurrencePattern;
    // Event type for templates
    eventType?: EventType;
    // Template-specific fields
    templateFields?: EventTemplateFields;
    // Sync-related properties
    isSynced?: boolean;
    syncSource?: string;
    syncId?: string;
    isReadOnly?: boolean;
}

export type EventType = 'CUSTOMER KEY DATE' | 'HR' | 'SALES' | 'EVENT' | 'PRODUCT RELEASE' | 'PRODUCT CODE FREEZE' | 'PRODUCT RND COMPLETE' | 'IST' | 'UAT' | 'GO LIVE' | 'PRODUCT SESSION' | 'OTHER';

export interface EventTemplateFields {    

    // Product Release fields
    version?: string;
    releaseType?: string;

    // Institution-specific fields (from Smartsheet)
    institution?: string;
    institutionPhase?: 'IST' | 'UAT' | 'GO LIVE';
}

export interface RecurrencePattern {
    type: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval: number;
    endDate?: Date;
    daysOfWeek?: number[]; // 0-6, where 0 is Sunday
}

export interface CalendarFilters {
    categories: string[];
    importance: string[];
    dateRange?: {
        start: Date;
        end: Date;
    };
}



export const EVENT_TYPE_COLORS = {
    'CUSTOMER KEY DATE': 'black',   // Black
    'HR': '#5c2d91',                // Purple
    'SALES': '#c7ad04',             // Orange
    'EVENT': '#fa464f',             // Red
    'PRODUCT RELEASE': 'blue',      // Blue
    'PRODUCT CODE FREEZE': '#0078d4', // Azure Blue
    'PRODUCT RND COMPLETE': '#8764b8', // Purple Blue
    'IST': '#ff8c00',               // Orange
    'UAT': '#ffaa44',               // Light Orange
    'GO LIVE': '#107c10',           // Green
    'CLOUD': '#fc0398',             // Pink
    'PRODUCT SESSION': '#26d9d6',   // Teal    
    'OTHER': 'grey',                // Grey

} as const;

export interface EventTemplate {
    id: EventType;
    name: string;
    description: string;
    fields: EventTemplateField[];
}

export interface EventTemplateField {
    name: string;
    type: 'text' | 'textarea' | 'select' | 'date' | 'datetime-local';
    label: string;
    required?: boolean;
    placeholder?: string;
    options?: string[];
    templateFieldKey?: keyof EventTemplateFields;
}

export const EVENT_TEMPLATES: EventTemplate[] = [
    {
        id: 'CUSTOMER KEY DATE',
        name: 'Customer Key Date',
        description: 'Important dates for customer milestones',
        fields: []
    },
    {
        id: 'HR',
        name: 'HR',
        description: 'Human resources related events',
        fields: []
    },
    {
        id: 'SALES',
        name: 'Sales',
        description: 'Sales related events',
        fields: []
    },
    {
        id: 'EVENT',
        name: 'Event',
        description: 'Key Event/Conference',
        fields: [
            {
                name: 'location',
                type: 'text',
                label: 'Location',
                required: false,
                placeholder: 'where in the world is this event taking place?'
            }
        ]
    },
    {
        id: 'PRODUCT RELEASE',
        name: 'Product Release',
        description: 'Product version releases and deployments',
        fields: [
            {
                name: 'version',
                type: 'text',
                label: 'Version Number',
                required: true,
                placeholder: 'e.g., v1.2.0, 2024.1',
                templateFieldKey: 'version'
            },
            {
                name: 'releaseType',
                type: 'text',
                label: 'Release Type',
                required: false,
                placeholder: 'e.g., Major, Minor, Hotfix',
                templateFieldKey: 'releaseType'
            }
        ]
    },
    {
        id: 'OTHER',
        name: 'Other',
        description: 'General purpose event with custom fields',
        fields: []
    }
];

// Smartsheet-specific types
export interface SmartsheetRow {
    id: number;
    rowNumber: number;
    cells: SmartsheetCell[];
}

export interface SmartsheetCell {
    columnId: number;
    value?: string | number | Date;
    displayValue?: string;
}

export interface SmartsheetSheet {
    id: number;
    name: string;
    columns: SmartsheetColumn[];
    rows: SmartsheetRow[];
}

export interface SmartsheetColumn {
    id: number;
    index: number;
    title: string;
    type: string;
    primary?: boolean;
}

export interface SmartsheetSyncConfig {
    sheetId: string;
    apiToken: string;
    columnMappings: {
        institution: string;
        istStartDate: string;
        istEndDate: string;
        uatStartDate: string;
        uatEndDate: string;
        goLiveDate: string;
    };
}

export const SYNC_SOURCES = {
    MANUAL: 'Manual',
    SMARTSHEET: 'Smartsheet',
    AZURE_DEVOPS: 'Azure DevOps',
} as const;

export type SyncSource = typeof SYNC_SOURCES[keyof typeof SYNC_SOURCES];
